/**
 * InstallmentPlanCard — Ratenplan-Vereinbarung im TenancyDetail.
 *
 * Verwalter kann:
 * - neuen Ratenplan anlegen (Gesamtsumme, Anzahl Raten, Startdatum)
 * - Zahlungseingänge pro Rate erfassen
 * - Status prüfen (überfällige Raten → Plan gebrochen)
 * - Plan abbrechen
 *
 * Mieter sieht nur den Status im Mieter-Portal (nicht hier).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatEUR, formatDate } from "@/lib/constants";
import { HandCoins, Plus, CheckCircle, Warning, XCircle, ArrowsClockwise } from "@phosphor-icons/react";

const STATUS_LABEL = { active: "Aktiv", completed: "Erfüllt", broken: "Gebrochen", cancelled: "Storniert" };
const STATUS_COLOR = {
  active: "bg-sky-100 text-sky-800 border-sky-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  broken: "bg-rose-100 text-rose-800 border-rose-200",
  cancelled: "bg-slate-200 text-slate-700 border-slate-300",
};

const CAT_DE = { rent_cold: "Kaltmiete", utilities: "Nebenkosten", heating: "Heizkosten", parking: "Stellplatz",
  vat: "USt", bka: "BKA", other: "Sonstiges", fee: "Gebühr", dunning_fee: "Mahngebühr", interest: "Verzugszinsen" };

// Angerechnete Zahlungen je Rate + Verknüpfungen aufheben
function PlanPayments({ plan, onChanged }) {
  const [pay, setPay] = useState(null);
  const load = () => api.get(`/rent-installments/${plan.id}/payments`).then(({ data }) => setPay(data.payments || {}))
    .catch(() => setPay({}));
  useEffect(() => { load(); }, [plan.id, plan.installments]); // eslint-disable-line react-hooks/exhaustive-deps
  const unlink = async (ids, label) => {
    if (!await window._domivusConfirm(`${label} aufheben? Die Buchungen bleiben im Mietkonto erhalten, zählen aber nicht mehr zum Ratenplan.`)) return;
    try {
      const { data } = await api.post(`/rent-installments/${plan.id}/unlink`, { posting_ids: ids });
      toast.success(`${data.unlinked} Verknüpfung(en) aufgehoben`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Aufheben fehlgeschlagen"); }
  };
  if (!pay) return null;
  const all = Object.values(pay).flat();
  return (
    <div className="mt-3 border-t border-slate-200 pt-2" data-testid={`plan-payments-${plan.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold text-slate-700">Angerechnete Zahlungen ({all.length})</div>
        {all.length > 0 && (
          <button type="button" className="text-[11px] text-rose-700 hover:underline" onClick={() => unlink([], "Alle Verknüpfungen dieses Plans")}
                  data-testid={`plan-unlink-all-${plan.id}`}>Plan bereinigen (alle aufheben)</button>
        )}
      </div>
      {all.length === 0 && <div className="text-[11px] text-slate-500 mt-1">Noch keine Zahlung mit diesem Plan verknüpft.</div>}
      {plan.installments.map((inst) => (pay[String(inst.index)] || []).length > 0 && (
        <div key={inst.index} className="mt-1.5">
          <div className="text-[11px] text-slate-600">Rate {inst.index} · fällig {formatDate(inst.due_date)}</div>
          {pay[String(inst.index)].map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 text-[11px] pl-2 py-0.5" data-testid={`plan-payment-${p.id}`}>
              <span className="min-w-0 truncate">
                {formatDate(p.posting_date)} · <b className="tabular-nums">{formatEUR(p.amount)}</b> → {CAT_DE[p.category] || p.category}
                {p.target_month ? ` ${p.target_month.slice(5, 7)}/${p.target_month.slice(0, 4)}` : ""}
                <span className="text-slate-400"> · {p.reference || "—"}</span>
              </span>
              <button type="button" className="text-rose-700 hover:underline shrink-0" onClick={() => unlink([p.id], "Verknüpfung dieser Zahlung")}
                      data-testid={`plan-unlink-${p.id}`}>aufheben</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function InstallmentPlanCard({ tenancyId, currentOutstanding, showAll = false, onChanged }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openPayment, setOpenPayment] = useState(null);       // {plan, installment}
  const [form, setForm] = useState({
    total_arrears_eur: "", installments_count: 3,
    installment_amount_eur: "",   // iter160e: optional statt count
    input_mode: "count",           // "count" | "amount"
    first_due_date: new Date().toISOString().slice(0, 10),
    note: "",
  });
  const [payForm, setPayForm] = useState({ paid_amount_eur: "" });
  const [busy, setBusy] = useState(false);
  // 2026-10 · Kompakter Modus: pro Plan-ID gemerkt, ob ausgeklappt
  const [expandedPlans, setExpandedPlans] = useState({});
  const toggleExpanded = (pid) => setExpandedPlans((prev) => ({ ...prev, [pid]: !prev[pid] }));

  const load = () => {
    if (!tenancyId) return;
    setLoading(true);
    api.get(`/rent-installments/${tenancyId}`)
      .then(({ data }) => setPlans(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [tenancyId]);
  useEffect(() => {
    if (openCreate && currentOutstanding > 0) {
      setForm((f) => ({ ...f, total_arrears_eur: String(currentOutstanding.toFixed(2)) }));
    }
  }, [openCreate, currentOutstanding]);

  const createPlan = async () => {
    setBusy(true);
    try {
      const total = Number(form.total_arrears_eur);
      const payload = {
        tenancy_id: tenancyId,
        total_arrears_eur: total,
        first_due_date: form.first_due_date,
        monthly_interval: true,
        note: form.note || null,
      };
      // 2026-10 · Backend berechnet Ratenzahl aus installment_amount_eur (mit
      // Schlussrate-Restbetrag). Client leitet nichts mehr ab.
      if (form.input_mode === "amount" && Number(form.installment_amount_eur) > 0) {
        payload.installment_amount_eur = Number(form.installment_amount_eur);
      } else {
        payload.installments_count = Number(form.installments_count);
      }
      await api.post("/rent-installments", payload);
      toast.success("Ratenplan angelegt. Mahnwesen für dieses MV pausiert.");
      setOpenCreate(false);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anlage fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const recordPayment = async () => {
    if (!openPayment) return;
    setBusy(true);
    try {
      await api.post(`/rent-installments/${openPayment.plan.id}/payment`, {
        plan_id: openPayment.plan.id,
        installment_index: openPayment.installment.index,
        paid_amount_eur: Number(payForm.paid_amount_eur),
      });
      toast.success("Zahlungseingang erfasst.");
      setOpenPayment(null); setPayForm({ paid_amount_eur: "" });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const checkStatus = async (planId) => {
    try {
      await api.post(`/rent-installments/${planId}/check`);
      toast.success("Status geprüft.");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const cancelPlan = async (planId) => {
    if (!await window._domivusConfirm("Ratenplan wirklich stornieren? Alle über \"+ Zahlung\" erfassten Ratenzahlungen werden entfernt. Bank-Ist-Buchungen werden nur entkoppelt (nicht gelöscht). Mahnwesen läuft danach wieder normal.")) return;
    try {
      await api.delete(`/rent-installments/${planId}`);
      toast.success("Ratenplan storniert und Verknüpfungen bereinigt.");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  // 2026-10 · Endgültiges Löschen (nur wenn bereits storniert / gebrochen / erfüllt)
  const purgePlan = async (planId) => {
    if (!await window._domivusConfirm("Diesen Ratenplan endgültig aus der Datenbank entfernen? Dieser Schritt kann nicht rückgängig gemacht werden. Die zugehörigen Ist-Buchungen bleiben (falls noch vorhanden) erhalten und werden nur entkoppelt.")) return;
    try {
      await api.delete(`/rent-installments/${planId}/purge`);
      toast.success("Ratenplan endgültig gelöscht.");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  // 2026-10 · Gebrochenen Plan reaktivieren (wenn nachträglich alle Raten bezahlt)
  const reactivatePlan = async (planId) => {
    try {
      await api.post(`/rent-installments/${planId}/reactivate`);
      toast.success("Ratenplan reaktiviert. Mahnstopp wieder aktiv.");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reaktivierung fehlgeschlagen");
    }
  };

  // Systemvorlagen (Brief-Vorlagen › Ratenzahlung) laden und Messaging-Dialog vorbereiten
  const openLetter = async (plan, kind) => {
    try {
      const { data } = await api.get(`/rent-installments/${plan.id}/letter-template`, { params: { kind } });
      window.dispatchEvent(new CustomEvent("domivus:compose-message", {
        detail: { tenancy_id: tenancyId, subject: data.subject, body: data.body,
          origin: `installment_${kind}`, origin_id: plan.id },
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vorlage konnte nicht geladen werden");
    }
  };

  const activePlan = plans.find((p) => p.status === "active");
  // iter159g — Startseite (showAll=false) zeigt NUR die aktive Vereinbarung;
  // ohne aktive Vereinbarung rendert die Komponente NICHTS (kein Info-Box).
  // Der Reiter „Ratenzahlung" (showAll=true) zeigt alle Historieneinträge.
  const visiblePlans = showAll ? plans : (activePlan ? [activePlan] : []);
  if (!showAll && !loading && !activePlan) {
    return null;
  }

  return (
    <div className="border border-slate-200 rounded-md p-4 bg-white" data-testid="installment-plans-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <HandCoins size={16} className="text-slate-700" />
          <span className="font-semibold text-slate-900">
            {showAll ? "Ratenplan-Historie" : "Aktive Ratenzahlung"}
          </span>
        </div>
        {(!activePlan || showAll) && (
          <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs"
                  onClick={() => setOpenCreate(true)} data-testid="installment-create-btn">
            <Plus size={12} /> Neu vereinbaren
          </Button>
        )}
      </div>

      {loading && <div className="text-xs text-slate-500 py-3">Lade …</div>}

      {!loading && visiblePlans.length === 0 && showAll && (
        <div className="text-xs text-slate-500 py-3">
          Noch keine Ratenzahlungsvereinbarung angelegt.
        </div>
      )}

      {!loading && visiblePlans.map((plan) => {
        const isOpen = !!expandedPlans[plan.id];
        const progressPct = Math.round((plan.progress || 0) * 100);
        const nextDue = plan.next_due;
        return (
        <div key={plan.id} className="border border-slate-100 rounded p-3 mb-2 bg-slate-50/40" data-testid={`installment-plan-${plan.id}`}>
          <div className="flex items-center justify-between gap-3">
            <button type="button"
                    onClick={() => toggleExpanded(plan.id)}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left hover:bg-slate-50 rounded px-1 -mx-1 py-0.5"
                    data-testid={`installment-toggle-${plan.id}`}>
              <span className="text-slate-500 text-xs font-mono">{isOpen ? "▾" : "▸"}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLOR[plan.status]} flex-shrink-0`}>
                {STATUS_LABEL[plan.status]}
              </span>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-600 min-w-0">
                {nextDue && plan.status === "active" && (
                  <span className={nextDue.is_overdue ? "text-rose-700 font-medium" : ""}>
                    Nächste Rate: <strong className="text-slate-900">{formatEUR(nextDue.amount_eur)}</strong>
                    <span className="text-slate-500"> am {formatDate(nextDue.due_date)}</span>
                    {nextDue.is_overdue && <span className="ml-1">· überfällig</span>}
                  </span>
                )}
                {plan.status === "broken" && plan.overdue_count > 0 && (
                  <span className="text-rose-700 font-medium">
                    {plan.overdue_count === 1 ? "1 Rate" : `${plan.overdue_count} Raten`} überfällig
                  </span>
                )}
                <span>Restbetrag: <strong className="text-slate-900 tabular-nums">{formatEUR(plan.remaining_eur ?? plan.total_arrears_eur)}</strong></span>
                <span className="text-slate-400 text-[10px]">{plan.paid_count}/{(plan.installments || []).length} bezahlt · {progressPct}%</span>
              </div>
            </button>
            <div className="flex items-center gap-1 flex-shrink-0">
              {plan.status === "active" && (
                <>
                  <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs"
                          onClick={() => openLetter(plan, "agreement")}
                          data-testid={`installment-confirm-letter-${plan.id}`}>
                    Bestätigung schreiben
                  </Button>
                  {nextDue?.is_overdue && (
                    <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs text-amber-800"
                            onClick={() => openLetter(plan, "reminder")}
                            data-testid={`installment-remind-active-${plan.id}`}>
                      Erinnerung schreiben
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs"
                          onClick={() => checkStatus(plan.id)}>
                    <ArrowsClockwise size={11} /> Status prüfen
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs text-rose-700"
                          onClick={() => cancelPlan(plan.id)}
                          data-testid={`installment-cancel-${plan.id}`}>
                    <XCircle size={11} /> Stornieren
                  </Button>
                </>
              )}
              {plan.status === "broken" && (
                <>
                  <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs text-amber-800"
                          onClick={() => openLetter(plan, "reminder")}
                          data-testid={`installment-remind-${plan.id}`}>
                    Erinnerung schreiben
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs text-sky-700"
                          onClick={() => reactivatePlan(plan.id)}
                          data-testid={`installment-reactivate-${plan.id}`}>
                    <ArrowsClockwise size={11} /> Reaktivieren
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs text-rose-700"
                          onClick={() => purgePlan(plan.id)}
                          data-testid={`installment-purge-${plan.id}`}>
                    Löschen
                  </Button>
                </>
              )}
              {plan.status === "completed" && (
                <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs text-emerald-700"
                        onClick={() => openLetter(plan, "completed")}
                        data-testid={`installment-completed-letter-${plan.id}`}>
                  Abschluss bestätigen
                </Button>
              )}
              {(plan.status === "cancelled" || plan.status === "completed") && (
                <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs text-rose-700"
                        onClick={() => purgePlan(plan.id)}
                        data-testid={`installment-purge-${plan.id}`}>
                  Löschen
                </Button>
              )}
            </div>
          </div>

          {/* Fortschrittsbalken */}
          <div className="w-full h-1 bg-slate-200 rounded overflow-hidden mt-2">
            <div className={`h-full ${plan.status === "broken" ? "bg-rose-400" : "bg-emerald-500"}`}
                 style={{ width: `${progressPct}%` }} />
          </div>

          {isOpen && (
          <>
          <table className="text-xs w-full mt-3">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500">
                <th className="py-1 pr-2 text-left font-normal">Rate</th>
                <th className="py-1 pr-2 text-left font-normal">Fällig</th>
                <th className="py-1 pr-2 text-right font-normal">Betrag</th>
                <th className="py-1 pr-2 text-right font-normal">Bezahlt</th>
                <th className="py-1 pr-2 text-left font-normal">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {plan.installments.map((inst) => (
                <tr key={inst.index} className="border-b border-slate-100 align-top">
                  <td className="py-1 pr-2 font-mono">{inst.index}</td>
                  <td className="py-1 pr-2">{formatDate(inst.due_date)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{formatEUR(inst.amount_eur)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums text-emerald-700">{formatEUR(inst.paid_amount_eur || 0)}</td>
                  <td className="py-1 pr-2">
                    {inst.status === "paid" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1">
                        <CheckCircle size={9} weight="fill" /> bezahlt
                      </span>
                    )}
                    {inst.status === "overdue" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200 inline-flex items-center gap-1">
                        <Warning size={9} weight="fill" /> überfällig
                      </span>
                    )}
                    {inst.status === "pending" && (
                      <span className="text-[10px] text-slate-500">offen</span>
                    )}
                    {inst.arrears_allocation && inst.arrears_allocation.length > 0 && (
                      <div className="text-[10px] text-emerald-700 mt-0.5 whitespace-normal break-words leading-snug"
                           data-testid={`inst-allocation-${plan.id}-${inst.index}`}>
                        {inst.arrears_allocation.map((a, i) => (
                          <div key={i}>→ {formatEUR(a.amount)} {
                            {rent_cold: "Kaltmiete", utilities: "Nebenkosten", heating: "Heizkosten",
                             parking: "Stellplatz", bka: "BKA"}[a.category] || a.category
                          } {a.period}</div>
                        ))}
                      </div>
                    )}
                    {inst.applied_to_hint && !(inst.arrears_allocation && inst.arrears_allocation.length > 0) && (
                      <div className="text-[10px] text-slate-400 mt-0.5 italic whitespace-normal break-words">{inst.applied_to_hint}</div>
                    )}
                  </td>
                  <td className="py-1 pl-2">
                    {plan.status === "active" && inst.status !== "paid" && (
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] gap-1"
                              onClick={() => { setOpenPayment({ plan, installment: inst });
                                              setPayForm({ paid_amount_eur: String(inst.amount_eur) }); }}
                              data-testid={`installment-pay-${plan.id}-${inst.index}`}>
                        + Zahlung
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PlanPayments plan={plan} onChanged={() => { load(); onChanged?.(); }} />
          {plan.broken_log && plan.broken_log.length > 0 && (
            <details className="mt-2 text-[11px]" data-testid={`broken-log-${plan.id}`}>
              <summary className="cursor-pointer text-rose-700 font-medium">Bruch-Log ({plan.broken_log.length})</summary>
              <ul className="mt-1 space-y-0.5 pl-3">
                {plan.broken_log.map((ev, i) => (
                  <li key={i} className="text-slate-600 whitespace-normal break-words">
                    {ev.event === "plan_broken" ? (
                      <>Plan gebrochen ({ev.overdue_count} Raten überfällig) am {formatDate(ev.detected_at)}</>
                    ) : (
                      <>Rate {ev.index} überfällig ({ev.days_late} Tage) — offen {formatEUR(ev.open_eur)} · erkannt {formatDate(ev.detected_at)}</>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {plan.note && <p className="text-[11px] text-slate-500 mt-2 italic whitespace-normal break-words">Notiz: {plan.note}</p>}
          </>
          )}
        </div>
        );
      })}

      {/* Create Dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-lg" data-testid="installment-create-dialog">
          <DialogHeader><DialogTitle>Neuen Ratenplan vereinbaren</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Gesamtrückstand (EUR)</Label>
              <Input type="number" step="0.01" value={form.total_arrears_eur}
                     onChange={(e) => setForm({ ...form, total_arrears_eur: e.target.value })}
                     data-testid="installment-total-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs flex items-center justify-between">
                  <span>Aufteilung nach</span>
                  <button type="button"
                          className="text-[10px] text-teal-700 hover:underline"
                          onClick={() => setForm({ ...form,
                            input_mode: form.input_mode === "count" ? "amount" : "count" })}
                          data-testid="installment-mode-toggle">
                    {form.input_mode === "count" ? "→ nach Ratenhöhe" : "→ nach Anzahl"}
                  </button>
                </Label>
                {form.input_mode === "count" ? (
                  <Input type="number" min="1" max="60" value={form.installments_count}
                         onChange={(e) => setForm({ ...form, installments_count: e.target.value })}
                         data-testid="installment-count-input"
                         placeholder="Anzahl Raten" />
                ) : (
                  <Input type="number" step="0.01" min="0.01"
                         value={form.installment_amount_eur}
                         onChange={(e) => setForm({ ...form, installment_amount_eur: e.target.value })}
                         data-testid="installment-amount-input"
                         placeholder="Ratenhöhe €/Monat" />
                )}
                {form.input_mode === "amount" && Number(form.installment_amount_eur) > 0 && Number(form.total_arrears_eur) > 0 && (() => {
                  const total = Number(form.total_arrears_eur);
                  const rate = Number(form.installment_amount_eur);
                  const n = Math.max(1, Math.ceil(total / rate));
                  const last = n === 1 ? total : Number((total - rate * (n - 1)).toFixed(2));
                  const hasSchluss = Math.abs(last - rate) > 0.005 && n > 1;
                  return (
                    <p className="text-[10px] text-slate-500 mt-1">
                      {n} Raten
                      {hasSchluss && <> · Schlussrate <b>{last.toFixed(2).replace(".", ",")} €</b></>}
                    </p>
                  );
                })()}
              </div>
              <div>
                <Label className="text-xs">Erste Rate fällig am</Label>
                <Input type="date" value={form.first_due_date}
                       onChange={(e) => setForm({ ...form, first_due_date: e.target.value })}
                       data-testid="installment-first-due-input" />
                <p className="text-[10px] text-slate-500 mt-1">
                  Datum darf in der Vergangenheit liegen — rückwirkende Vereinbarung möglich.
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Notiz (optional)</Label>
              <Input value={form.note}
                     onChange={(e) => setForm({ ...form, note: e.target.value })}
                     placeholder="z. B. Vereinbart per Telefon 14.02.2026" />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
              <Warning size={12} className="inline mr-1" />
              Solange die Raten pünktlich (Toleranz 3 Tage) eingehen, wird das Mahnwesen für
              dieses Mietverhältnis pausiert. Bei 2 oder mehr überfälligen Raten wird der
              Plan als &bdquo;gebrochen&ldquo; markiert und die Eskalation läuft wieder normal.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenCreate(false)} disabled={busy}>Abbrechen</Button>
            <Button onClick={createPlan} disabled={busy || !form.total_arrears_eur}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="installment-create-submit">
              {busy ? "Erstelle …" : "Ratenplan anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={!!openPayment} onOpenChange={(v) => !v && setOpenPayment(null)}>
        <DialogContent className="max-w-sm" data-testid="installment-payment-dialog">
          <DialogHeader><DialogTitle>Zahlung erfassen</DialogTitle></DialogHeader>
          {openPayment && (
            <div className="space-y-3 py-2">
              <p className="text-xs text-slate-600">
                Rate {openPayment.installment.index} · fällig {formatDate(openPayment.installment.due_date)} ·
                Betrag {formatEUR(openPayment.installment.amount_eur)}
              </p>
              <div>
                <Label className="text-xs">Eingegangener Betrag</Label>
                <Input type="number" step="0.01" value={payForm.paid_amount_eur}
                       onChange={(e) => setPayForm({ paid_amount_eur: e.target.value })}
                       data-testid="installment-payment-amount" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenPayment(null)} disabled={busy}>Abbrechen</Button>
            <Button onClick={recordPayment} disabled={busy}
                    className="bg-emerald-700 hover:bg-emerald-600"
                    data-testid="installment-payment-submit">
              {busy ? "Speichere …" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
